// The run-screen instrument strip: one cell per reading, updated in place at ~10 Hz. Units are always shown.
import type { SimState, Domain } from '../types';
import type { ResolvedCraft } from '../physics/resolve';
import { isJet } from '../physics/resolve';
import { h } from './dom';
import { fmtFuel, fmtFlow, fmtSpecificRange, fmtKt, fmtFt, fmtRpm, fmtPct, fmtNm, fmtHours } from './format';

export interface Instruments {
  root: HTMLElement;
  update(s: SimState, warp: number, auto: boolean, capacityKg: number): void;
}

interface Inst { value: HTMLElement; sub: HTMLElement }

function inst(root: HTMLElement, label: string): Inst {
  const value = h('span.cell-value', {}, '—');
  const sub = h('span.cell-sub', {}, '');
  root.append(h('div.cell', {}, h('span.cell-label', {}, label), value, sub));
  return { value, sub };
}

export function makeInstruments(rc: ResolvedCraft, domain: Domain, big: boolean): Instruments {
  const root = h('div.inst', { role: 'group', 'aria-label': 'Instruments' });
  const air = !!rc.air;
  const jet = air && isJet(rc);
  const fuel = inst(root, 'Fuel');
  const sr = inst(root, 'Specific range');
  const gs = inst(root, 'Groundspeed');
  const alt = inst(root, air ? 'Altitude' : 'Through water');
  const eng = inst(root, jet ? 'Thrust' : 'Engine');
  const dist = inst(root, 'Distance');
  const time = inst(root, 'Sim time');
  const warp = inst(root, 'Warp');
  return {
    root,
    update(s, w, auto, capacityKg) {
      fuel.value.textContent = fmtFuel(s.fuelKg, domain, big);
      fuel.sub.textContent = `${fmtFlow(s.fuelFlowKgPerH, domain, big)} · ${fmtPct(capacityKg > 0 ? s.fuelKg / capacityKg : 0)}`;
      sr.value.textContent = fmtSpecificRange(s.specificRangeNmPerKg, domain, big);
      sr.sub.textContent = air ? (s.phase === 'climb' ? 'climbing' : 'cruise') : `sea state ${s.seaState}`;
      gs.value.textContent = fmtKt(s.groundKt);
      const comp = Math.round(s.windOrCurrentKt);
      gs.sub.textContent = air
        ? `${fmtKt(s.tasKt)} TAS · ${comp >= 0 ? 'tail' : 'head'}wind ${Math.abs(comp)} kt`
        : `current ${comp >= 0 ? '+' : '−'}${Math.abs(s.windOrCurrentKt).toFixed(1)} kn`;
      alt.value.textContent = air ? fmtFt(s.altitudeFt) : `${s.tasKt.toFixed(1)} kn`;
      alt.sub.textContent = air ? (s.storesJettisoned ? 'external tanks dropped' : '') : `fouling ${fmtPct(s.fouling)} power`;
      if (jet) {
        eng.value.textContent = fmtPct(s.load);
        eng.sub.textContent = 'of available thrust';
      } else {
        eng.value.textContent = fmtRpm(s.rpm);
        eng.sub.textContent = `${fmtPct(s.load)} load`;
      }
      dist.value.textContent = fmtNm(s.distanceNm);
      dist.sub.textContent = `segment ${s.segmentIndex + 1}`;
      time.value.textContent = fmtHours(s.timeS / 3600);
      time.sub.textContent = `day ${Math.floor(s.timeS / 86400) + 1}`;
      warp.value.textContent = `×${w}`;
      warp.sub.textContent = auto ? 'auto' : 'manual';
    },
  };
}
